import { Operation } from '@apollo/client'
import * as Sentry from '@sentry/react'
import { equalsIgnoreCase } from '../../helpers/stringUtils'
import { GRAPHQL_ERROR_MESSAGE } from '../../monitoring/constants'

interface GraphQLErrorTelemetryError {
  message?: string
  path?: readonly unknown[]
  extensions?: {
    code?: unknown
    status?: unknown
    originalError?: { message?: unknown } | null
  } | null
}

type GraphQLErrorTelemetryOperation = Pick<Operation, 'operationName' | 'variables'>

const ORGANIZATION_NOT_FOUND_MESSAGE = 'No Organization found'
const NOT_FOUND_MESSAGE = 'Not Found'
const SUBDOMAIN_LOOKUP_PATH = 'organizationBySubdomain'
const ORGANIZATION_NOT_FOUND_HTTP_STATUS = 404

// Extract the human-readable validation message(s) from a GraphQL error.
// NestJS' ValidationPipe nests them at `extensions.originalError.message`
// (a string or string[]). We surface them at a shallow Sentry `extra` key so
// they aren't truncated to "[Array]" by Sentry's default `normalizeDepth`.
export const extractValidationMessages = (
  err: GraphQLErrorTelemetryError | undefined,
): string | undefined => {
  const raw = err?.extensions?.originalError?.message
  if (Array.isArray(raw)) {
    const messages = raw.filter((message): message is string => typeof message === 'string')
    return messages.length > 0 ? messages.join('; ') : undefined
  }
  return typeof raw === 'string' ? raw : undefined
}

// Visitors hitting a subdomain that doesn't resolve to an organization (decommissioned
// tenant, stale DNS, typo, bot traffic) trigger a 404 / "No Organization found" on every
// subdomain-scoped query/mutation. This is expected behavior, not an actionable error, so
// we record a breadcrumb instead of flooding Sentry. The `subdomain` variable guard keeps
// us from suppressing a genuine "No Organization found" raised outside the subdomain-lookup flow.
export const isOrganizationNotFoundError = (
  err: GraphQLErrorTelemetryError | undefined,
  operation: GraphQLErrorTelemetryOperation,
): boolean => {
  if (operation.variables?.subdomain == null) return false
  if (equalsIgnoreCase(err?.message, ORGANIZATION_NOT_FOUND_MESSAGE)) return true
  return (
    equalsIgnoreCase(err?.message, NOT_FOUND_MESSAGE) &&
    (equalsIgnoreCase(err?.path?.[0], SUBDOMAIN_LOOKUP_PATH) ||
      err?.extensions?.status === ORGANIZATION_NOT_FOUND_HTTP_STATUS)
  )
}

export const emitGraphQLErrorTelemetry = (
  err: GraphQLErrorTelemetryError | undefined,
  operation: GraphQLErrorTelemetryOperation,
): void => {
  if (isOrganizationNotFoundError(err, operation)) {
    Sentry.addBreadcrumb({
      category: 'graphql',
      message: 'Org-not-found for subdomain - skipping Sentry capture',
      level: 'info',
      data: {
        operation: operation.operationName,
        subdomain: operation.variables?.subdomain,
        errorMessage: err?.message,
        errorPath: err?.path,
      },
    })
    return
  }

  Sentry.withScope((scope) => {
    scope.setLevel('info')
    scope.setFingerprint(['graphql-error', operation.operationName, String(err?.extensions?.code ?? 'unknown')])
    Sentry.captureException(new Error(GRAPHQL_ERROR_MESSAGE), {
      extra: {
        operation: operation.operationName,
        variables: operation.variables,
        errorMessage: err?.message,
        errorCode: err?.extensions?.code,
        errorPath: err?.path,
        // Shallow key so it survives Sentry's normalizeDepth (the nested
        // errorExtensions.originalError.message is otherwise dropped as "[Array]").
        validationMessages: extractValidationMessages(err),
        errorExtensions: err?.extensions,
      },
    })
  })
}
